class AnalyticsService {
  constructor() {
    this.apiUrl = process.env.REACT_APP_ANALYTICS_API_URL;
    this.apiKey = process.env.REACT_APP_API_KEY;
    this.lastRequestTime = 0;
    this.minRequestInterval = 1000; // Minimum 1 second between requests
  }

  generateNonce() {
    // Generate a random nonce using crypto API
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  async generateSignature(payload, timestamp, nonce) {
    try {
      // Sort the payload keys to ensure consistent ordering
      const sortedPayload = {};
      Object.keys(payload).sort().forEach(key => {
        sortedPayload[key] = payload[key];
      });

      // Create a message containing payload, timestamp, and nonce
      const message = JSON.stringify({
        body: sortedPayload,
        timestamp: timestamp.toString(),
        nonce: nonce
      });
      
      // Use SubtleCrypto API for HMAC in the browser
      const key = await crypto.subtle.importKey(
        'raw',
        new TextEncoder().encode(this.apiKey.trim()),
        { name: 'HMAC', hash: 'SHA-256' },
        false,
        ['sign']
      );
      
      const signatureBuffer = await crypto.subtle.sign(
        'HMAC',
        key,
        new TextEncoder().encode(message)
      );
      
      // Convert the ArrayBuffer to hex string
      return Array.from(new Uint8Array(signatureBuffer))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
    } catch (error) {
      console.error('Error generating signature:', error);
      throw error;
    }
  }

  async recordSearch(storeUrl, success, freeProductsCount, processingTime, errorMessage) {
    // Check if enough time has passed since last request
    const now = Date.now();
    if (now - this.lastRequestTime < this.minRequestInterval) {
      return; // Skip this analytics event if we're sending too many
    }

    try {
      this.lastRequestTime = now;
      
      const payload = {
        eventType: 'single_search',
        storeUrl: storeUrl || null,
        success: Boolean(success),
        freeProductsCount: freeProductsCount || 0,
        processingTime: processingTime || 0,
        errorMessage: errorMessage || null
      };

      return await this.sendAnalytics(payload);
    } catch (error) {
      console.error('Failed to record single search analytics:', error);
      return null;
    }
  }

  async recordBatchSearch(batchData, processingTime, wasManuallyStopped = false) {
    try {
      const payload = {
        eventType: 'batch_search',
        totalLinksProcessed: batchData.totalLinksProcessed,
        validStoresFound: batchData.validStoresFound,
        storesWithFreeProducts: batchData.storesWithFreeProducts,
        totalFreeProducts: batchData.results.reduce((sum, store) => sum + store.freeProducts.length, 0),
        success: batchData.success,
        processingTime: processingTime,
        wasManuallyStopped: wasManuallyStopped
      };

      return await this.sendAnalytics(payload);
    } catch (error) {
      console.error('Failed to record batch search analytics:', error);
      return null;
    }
  }

  async sendAnalytics(payload) {
    try {
      const now = Date.now();
      const nonce = this.generateNonce();
      const signature = await this.generateSignature(payload, now, nonce);

      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': this.apiKey,
          'x-timestamp': now.toString(),
          'x-signature': signature,
          'x-nonce': nonce
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Analytics API error: ${await response.text()}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to send analytics:', error);
      return null;
    }
  }
}

// Create a singleton instance
export const analyticsService = new AnalyticsService();